// src/errors.ts
var XONE_CSS_ERROR_CODES = {
  /** Sintaxis CSS XOne malformada (llaves, dos puntos, valores invalidos). */
  SYNTAX_ERROR: "XONE.CSS.SYNTAX_ERROR",
  /** Unidad no permitida (px, em, rem, vh, vw, pt, cm, mm, in). */
  INVALID_UNIT: "XONE.CSS.INVALID_UNIT",
  /** Color hexadecimal invalido (longitud o caracteres). */
  INVALID_COLOR: "XONE.CSS.INVALID_COLOR",
  /** Ciclo detectado al resolver extends. */
  CIRCULAR_EXTENDS: "XONE.CSS.CIRCULAR_EXTENDS",
  /** Referencia extends a regla inexistente. */
  UNRESOLVED_EXTENDS: "XONE.CSS.UNRESOLVED_EXTENDS",
  /** Bloque @platform con etiqueta no reconocida. */
  INVALID_PLATFORM: "XONE.CSS.INVALID_PLATFORM"
};
var XoneCssError = class _XoneCssError extends Error {
  code;
  source;
  cause;
  constructor(message, options) {
    super(message);
    this.name = "XoneCssError";
    this.code = options.code;
    this.source = options.source === void 0 ? void 0 : options.source.length > 120 ? options.source.slice(0, 120) + "..." : options.source;
    this.cause = options.cause;
    Object.setPrototypeOf(this, _XoneCssError.prototype);
  }
};

// src/selectors.ts
var PROP_TYPE_RE = /^prop:([A-Za-z][A-Za-z0-9]*)$/;
var CLASS_RE = /^\.([A-Za-z_][\w-]*)$/;
var SIMPLE_RE = /^(coll|group|frame|prop)$/;
function parseSelector(raw) {
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    throw new XoneCssError("Selector vacio.", {
      code: XONE_CSS_ERROR_CODES.SYNTAX_ERROR,
      source: raw
    });
  }
  if (trimmed.includes(",")) {
    const parts = trimmed.split(",").map((p) => p.trim()).filter((p) => p.length > 0);
    if (parts.length === 0) {
      throw new XoneCssError("Selector compuesto vacio.", {
        code: XONE_CSS_ERROR_CODES.SYNTAX_ERROR,
        source: raw
      });
    }
    if (parts.length === 1) {
      return parseSelector(parts[0]);
    }
    return {
      type: "compound",
      selectors: parts.map((p) => parseSelector(p))
    };
  }
  if (SIMPLE_RE.test(trimmed)) {
    return { type: trimmed };
  }
  const propTypeMatch = PROP_TYPE_RE.exec(trimmed);
  if (propTypeMatch) {
    return { type: "prop-type", propType: propTypeMatch[1] };
  }
  const classMatch = CLASS_RE.exec(trimmed);
  if (classMatch) {
    return { type: "class", className: classMatch[1] };
  }
  throw new XoneCssError(`Selector no reconocido: "${trimmed}".`, {
    code: XONE_CSS_ERROR_CODES.SYNTAX_ERROR,
    source: raw
  });
}
function selectorToCss(ast) {
  switch (ast.type) {
    case "coll":
      return ".xone-coll";
    case "group":
      return ".xone-group";
    case "frame":
      return ".xone-frame";
    case "prop":
      return ".xone-prop";
    case "prop-type":
      return `.xone-prop-${ast.propType.toLowerCase()}`;
    case "class":
      return `.${ast.className}`;
    case "compound":
      return ast.selectors.map((s) => selectorToCss(s)).join(", ");
  }
}
function selectorKey(ast) {
  switch (ast.type) {
    case "coll":
    case "group":
    case "frame":
    case "prop":
      return ast.type;
    case "prop-type":
      return `prop:${ast.propType}`;
    case "class":
      return `.${ast.className}`;
    case "compound":
      return ast.selectors.map((s) => selectorKey(s)).join(",");
  }
}

// src/platform.ts
var VALID_PLATFORMS = [
  "phone",
  "tablet",
  "watch",
  "web",
  "ios",
  "android",
  "portrait",
  "landscape",
  "day",
  "night"
];
var ORIENTATION_TAGS = /* @__PURE__ */ new Set(["portrait", "landscape"]);
var THEME_TAGS = /* @__PURE__ */ new Set(["day", "night"]);
function assertValidPlatformTag(tag) {
  if (!VALID_PLATFORMS.includes(tag)) {
    throw new XoneCssError(
      `Plataforma desconocida "${tag}". Validas: ${VALID_PLATFORMS.join(", ")}.`,
      {
        code: XONE_CSS_ERROR_CODES.INVALID_PLATFORM,
        source: `@platform:${tag}`
      }
    );
  }
}
function platformTagToMediaQuery(tag) {
  switch (tag) {
    case "phone":
      return "(max-width: 767px)";
    case "tablet":
      return "(min-width: 768px) and (max-width: 1199px)";
    case "watch":
      return "(max-width: 320px)";
    case "web":
      return "(min-width: 1200px)";
    case "portrait":
      return "(orientation: portrait)";
    case "landscape":
      return "(orientation: landscape)";
    case "day":
      return "(prefers-color-scheme: light)";
    case "night":
      return "(prefers-color-scheme: dark)";
    case "ios":
    case "android":
      return null;
  }
}
function matchesPlatform(tag, ctx) {
  if (!ctx) {
    return true;
  }
  if (ORIENTATION_TAGS.has(tag)) {
    return ctx.orientation === void 0 || ctx.orientation === tag;
  }
  if (THEME_TAGS.has(tag)) {
    return ctx.theme === void 0 || ctx.theme === tag;
  }
  if (ctx.platform === void 0) {
    return true;
  }
  return ctx.platform === tag;
}

// src/parser.ts
function stripComments(source) {
  return source.replace(/\/\*[\s\S]*?\*\//g, "");
}
function skipWhitespace(state) {
  while (state.index < state.source.length) {
    const ch = state.source[state.index];
    if (ch === " " || ch === "	" || ch === "\n" || ch === "\r") {
      state.index++;
    } else {
      break;
    }
  }
}
function readUntil(state, terminators) {
  const start = state.index;
  while (state.index < state.source.length) {
    const ch = state.source[state.index];
    if (terminators.includes(ch)) {
      break;
    }
    state.index++;
  }
  return state.source.slice(start, state.index);
}
function expect(state, ch) {
  if (state.source[state.index] !== ch) {
    throw new XoneCssError(
      `Se esperaba "${ch}" pero se encontro "${state.source[state.index] ?? "EOF"}".`,
      {
        code: XONE_CSS_ERROR_CODES.SYNTAX_ERROR,
        source: state.source.slice(Math.max(0, state.index - 20), state.index + 20)
      }
    );
  }
  state.index++;
}
function parseDeclarationsBlock(state) {
  expect(state, "{");
  const declarations = [];
  let extendsFrom;
  while (state.index < state.source.length) {
    skipWhitespace(state);
    if (state.source[state.index] === "}") {
      state.index++;
      return { declarations, extendsFrom };
    }
    const property = readUntil(state, ":};").trim();
    if (property.length === 0) {
      if (state.source[state.index] === ";") {
        state.index++;
        continue;
      }
      throw new XoneCssError("Propiedad CSS vacia.", {
        code: XONE_CSS_ERROR_CODES.SYNTAX_ERROR,
        source: state.source.slice(state.index, state.index + 40)
      });
    }
    if (state.source[state.index] !== ":") {
      throw new XoneCssError(
        `Se esperaba ":" tras la propiedad "${property}".`,
        {
          code: XONE_CSS_ERROR_CODES.SYNTAX_ERROR,
          source: state.source.slice(
            Math.max(0, state.index - 20),
            state.index + 20
          )
        }
      );
    }
    state.index++;
    const value = readUntil(state, ";}").trim();
    if (state.source[state.index] === ";") {
      state.index++;
    }
    if (property.toLowerCase() === "extends") {
      extendsFrom = value;
    } else {
      declarations.push({ property, value });
    }
  }
  throw new XoneCssError('Bloque sin cerrar (falta "}").', {
    code: XONE_CSS_ERROR_CODES.SYNTAX_ERROR
  });
}
function parseRule(state) {
  const selectorText = readUntil(state, "{").trim();
  if (selectorText.length === 0) {
    throw new XoneCssError("Selector vacio.", {
      code: XONE_CSS_ERROR_CODES.SYNTAX_ERROR,
      source: state.source.slice(state.index, state.index + 40)
    });
  }
  const selector = parseSelector(selectorText);
  const { declarations, extendsFrom } = parseDeclarationsBlock(state);
  return {
    selector,
    rawSelector: selectorText,
    declarations,
    extendsFrom
  };
}
function parsePlatformBlock(state) {
  const header = readUntil(state, "{").trim();
  const match = /^platform\s*:\s*([A-Za-z][A-Za-z0-9_-]*)$/.exec(header);
  if (!match) {
    throw new XoneCssError(
      `Cabecera @platform malformada: "@${header}".`,
      {
        code: XONE_CSS_ERROR_CODES.SYNTAX_ERROR,
        source: header
      }
    );
  }
  const tag = match[1];
  assertValidPlatformTag(tag);
  expect(state, "{");
  const rules = [];
  while (state.index < state.source.length) {
    skipWhitespace(state);
    if (state.source[state.index] === "}") {
      state.index++;
      return { platform: tag, rules };
    }
    if (state.source[state.index] === "@") {
      throw new XoneCssError(
        "@platform no se puede anidar dentro de otro @platform.",
        {
          code: XONE_CSS_ERROR_CODES.SYNTAX_ERROR,
          source: state.source.slice(state.index, state.index + 40)
        }
      );
    }
    rules.push(parseRule(state));
  }
  throw new XoneCssError("Bloque @platform sin cerrar.", {
    code: XONE_CSS_ERROR_CODES.SYNTAX_ERROR
  });
}
function parseXoneCss(source) {
  if (typeof source !== "string") {
    throw new XoneCssError("La fuente CSS debe ser una cadena.", {
      code: XONE_CSS_ERROR_CODES.SYNTAX_ERROR
    });
  }
  const clean = stripComments(source);
  const state = { source: clean, index: 0 };
  const rules = [];
  const platformBlocks = [];
  while (state.index < state.source.length) {
    skipWhitespace(state);
    if (state.index >= state.source.length) {
      break;
    }
    const ch = state.source[state.index];
    if (ch === "@") {
      state.index++;
      platformBlocks.push(parsePlatformBlock(state));
      continue;
    }
    rules.push(parseRule(state));
  }
  return { rules, platformBlocks };
}

// src/extends.ts
function indexRules(rules) {
  const map = /* @__PURE__ */ new Map();
  for (const rule of rules) {
    const key = selectorKey(rule.selector);
    if (rule.selector.type === "compound") {
      for (const sub of rule.selector.selectors) {
        map.set(selectorKey(sub), rule);
      }
    } else {
      map.set(key, rule);
    }
  }
  return map;
}
function resolveRule(rule, index, visited, opts) {
  const key = selectorKey(rule.selector);
  if (visited.has(key)) {
    throw new XoneCssError(
      `Ciclo en extends detectado para "${rule.rawSelector}" (cadena: ${[...visited, key].join(" -> ")}).`,
      {
        code: XONE_CSS_ERROR_CODES.CIRCULAR_EXTENDS,
        source: rule.rawSelector
      }
    );
  }
  visited.add(key);
  const result = /* @__PURE__ */ new Map();
  if (rule.extendsFrom !== void 0) {
    const parentKey = rule.extendsFrom.trim();
    const parent = index.get(parentKey);
    if (!parent) {
      if (opts.lenient === true) {
        const msg = `[xone-css-engine] extends no resuelto: "${parentKey}" (referenciado desde "${rule.rawSelector}"). Se omite la herencia.`;
        if (opts.onWarning) opts.onWarning(msg);
        else console.warn(msg);
      } else {
        throw new XoneCssError(
          `extends no resuelto: "${parentKey}" (referenciado desde "${rule.rawSelector}").`,
          {
            code: XONE_CSS_ERROR_CODES.UNRESOLVED_EXTENDS,
            source: rule.rawSelector
          }
        );
      }
    } else {
      const parentDecls = resolveRule(parent, index, visited, opts);
      for (const [k, v] of parentDecls) {
        result.set(k, v);
      }
    }
  }
  for (const decl of rule.declarations) {
    result.set(decl.property, decl.value);
  }
  visited.delete(key);
  return result;
}
function resolveExtends(ast, options = {}) {
  const allRules = [...ast.rules];
  for (const block of ast.platformBlocks) {
    allRules.push(...block.rules);
  }
  const index = indexRules(allRules);
  const resolveAll = (rules) => rules.map((rule) => ({
    ...rule,
    resolvedDeclarations: resolveRule(rule, index, /* @__PURE__ */ new Set(), options)
  }));
  return {
    rules: resolveAll(ast.rules),
    platformBlocks: ast.platformBlocks.map((block) => ({
      platform: block.platform,
      rules: resolveAll(block.rules)
    }))
  };
}

// src/units.ts
var FORBIDDEN_UNITS = ["px", "em", "rem", "vh", "vw", "vmin", "vmax", "pt", "cm", "mm", "in"];
var VALUE_TOKEN_RE = /(-?\d*\.?\d+)([a-zA-Z%]+)/g;
function transformUnits(value, contextProperty, options = {}) {
  if (typeof value !== "string") {
    return value;
  }
  const strict = options.strict !== false;
  return value.replace(VALUE_TOKEN_RE, (_match, num, unit) => {
    const lower = unit.toLowerCase();
    if (lower === "p") {
      return `${num}px`;
    }
    if (lower === "%") {
      return `${num}%`;
    }
    if (FORBIDDEN_UNITS.includes(lower)) {
      if (strict) {
        throw new XoneCssError(
          `Unidad CSS prohibida en XOne: "${unit}". Usa "p" (puntos) o "%".`,
          {
            code: XONE_CSS_ERROR_CODES.INVALID_UNIT,
            source: contextProperty ? `${contextProperty}: ${value}` : value
          }
        );
      }
      if (options.onForbiddenUnit) {
        options.onForbiddenUnit({ unit, value });
      }
      return `${num}${unit}`;
    }
    return `${num}${unit}`;
  });
}

// src/colors.ts
var HEX_COLOR_RE = /#([0-9A-Fa-f]{8}|[0-9A-Fa-f]{6})\b/g;
function transformColors(value, contextProperty) {
  if (typeof value !== "string") {
    return value;
  }
  const invalidHex = /#([0-9A-Fa-f]+)/g;
  let m;
  while ((m = invalidHex.exec(value)) !== null) {
    const hex = m[1];
    if (hex.length !== 6 && hex.length !== 8) {
      throw new XoneCssError(
        `Color hex invalido "#${hex}". XOne acepta 6 (RRGGBB) u 8 (AARRGGBB) digitos.`,
        {
          code: XONE_CSS_ERROR_CODES.INVALID_COLOR,
          source: contextProperty ? `${contextProperty}: ${value}` : value
        }
      );
    }
  }
  return value.replace(HEX_COLOR_RE, (_match, hex) => {
    if (hex.length === 6) {
      return `#${hex.toUpperCase()}`;
    }
    const aa = hex.slice(0, 2);
    const rr = hex.slice(2, 4);
    const gg = hex.slice(4, 6);
    const bb = hex.slice(6, 8);
    return `#${(rr + gg + bb + aa).toUpperCase()}`;
  });
}

// src/transformer.ts
var PROPERTY_MAP = {
  bgcolor: "background-color",
  forecolor: "color",
  "text-forecolor": "color",
  "text-bgcolor": "background-color",
  fontsize: "font-size",
  "text-fontsize": "font-size",
  "label-fontsize": "font-size",
  fontname: "font-family",
  fontbold: "font-weight",
  fontitalic: "font-style",
  "text-align": "text-align",
  lmargin: "margin-left",
  rmargin: "margin-right",
  tmargin: "margin-top",
  bmargin: "margin-bottom",
  lpadding: "padding-left",
  rpadding: "padding-right",
  tpadding: "padding-top",
  bpadding: "padding-bottom",
  "border-corner-radius": "border-radius",
  "border-color": "border-color",
  "border-width": "border-width"
};
function mapValue(property, rawValue, unitsOpts = { strict: false }) {
  if (property === "visible") {
    if (rawValue.trim() === "0") {
      return { property: "display", value: "none" };
    }
    return null;
  }
  if (property === "fontbold" || property === "textfont-bold" || property === "label-fontbold") {
    const v = rawValue.trim().toLowerCase();
    return { property: "font-weight", value: v === "true" ? "bold" : "normal" };
  }
  if (property === "fontitalic" || property === "textfont-italic" || property === "label-fontitalic") {
    const v = rawValue.trim().toLowerCase();
    return { property: "font-style", value: v === "true" ? "italic" : "normal" };
  }
  if (property === "framebox") {
    const v = rawValue.trim().toLowerCase();
    if (v === "false") return { property: "border", value: "none" };
    return null;
  }
  const mappedProp = PROPERTY_MAP[property] ?? property;
  let value = rawValue;
  value = transformColors(value, property);
  value = transformUnits(value, property, unitsOpts);
  if (property === "fontsize" && /^-?\d+(\.\d+)?$/.test(value.trim())) {
    value = `${value.trim()}px`;
  }
  if ((property === "border-corner-radius" || property === "border-width") && /^-?\d+(\.\d+)?$/.test(value.trim())) {
    value = `${value.trim()}px`;
  }
  return { property: mappedProp, value };
}
function emitRule(rule, pretty, indent, unitsOpts) {
  const selector = selectorToCss(rule.selector);
  const lines = [];
  for (const [prop, value] of rule.resolvedDeclarations) {
    const mapped = mapValue(prop, value, unitsOpts);
    if (!mapped) continue;
    lines.push(`${indent}    ${mapped.property}: ${mapped.value};`);
  }
  if (lines.length === 0) {
    return null;
  }
  if (pretty) {
    return `${indent}${selector} {
${lines.join("\n")}
${indent}}`;
  }
  return `${selector}{${lines.map((l) => l.trim()).join("")}}`;
}
function compileAst(resolved, options) {
  const pretty = options.pretty !== false;
  const strategy = options.platformStrategy ?? "media-query";
  const target = options.target;
  const unitsOpts = {
    strict: options.strictUnits === true,
    ...options.onForbiddenUnit !== void 0 ? { onForbiddenUnit: options.onForbiddenUnit } : {}
  };
  const out = [];
  for (const rule of resolved.rules) {
    const css = emitRule(rule, pretty, "", unitsOpts);
    if (css) out.push(css);
  }
  for (const block of resolved.platformBlocks) {
    const tag = block.platform;
    if (!matchesPlatform(tag, target)) {
      continue;
    }
    if (strategy === "filter") {
      for (const rule of block.rules) {
        const css = emitRule(rule, pretty, "", unitsOpts);
        if (css) out.push(css);
      }
      continue;
    }
    const mq = platformTagToMediaQuery(tag);
    if (mq === null) {
      if (target?.platform === tag) {
        for (const rule of block.rules) {
          const css = emitRule(rule, pretty, "", unitsOpts);
          if (css) out.push(css);
        }
      }
      continue;
    }
    const inner = [];
    for (const rule of block.rules) {
      const css = emitRule(rule, pretty, "    ", unitsOpts);
      if (css) inner.push(css);
    }
    if (inner.length === 0) continue;
    if (pretty) {
      out.push(`@media ${mq} {
${inner.join("\n")}
}`);
    } else {
      out.push(`@media ${mq}{${inner.map((l) => l.replace(/^\s+/, "")).join("")}}`);
    }
  }
  return pretty ? out.join("\n\n") : out.join("");
}
function transformToStandardCss(xoneSource, options = {}) {
  try {
    const ast = parseXoneCss(xoneSource);
    const lenient = options.lenientExtends !== false;
    const resolved = resolveExtends(ast, {
      lenient,
      ...options.onUnresolvedExtends !== void 0 ? { onWarning: options.onUnresolvedExtends } : {}
    });
    return compileAst(resolved, options);
  } catch (err) {
    if (err instanceof XoneCssError) {
      throw err;
    }
    throw new XoneCssError(
      err instanceof Error ? err.message : "Error desconocido al transformar CSS XOne.",
      {
        code: "XONE.CSS.SYNTAX_ERROR",
        cause: err instanceof Error ? err : void 0
      }
    );
  }
}
export {
  FORBIDDEN_UNITS,
  VALID_PLATFORMS,
  XONE_CSS_ERROR_CODES,
  XoneCssError,
  assertValidPlatformTag,
  matchesPlatform,
  parseSelector,
  parseXoneCss,
  platformTagToMediaQuery,
  resolveExtends,
  selectorKey,
  selectorToCss,
  transformColors,
  transformToStandardCss,
  transformUnits
};
//# sourceMappingURL=index.js.map